import { queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  EmptyState,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  TASK_STATUS_TONE,
} from '@mevaser/ui'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { CornerDownRight, ListChecks, Pencil, Plus, Search } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { type CustomerLite, CustomerPicker } from '../components/customer-picker.js'
import { DataTable } from '../components/data-table.js'
import { ExpandToggle, TaskNotesPreview } from '../components/notes-preview.js'
import {
  CompleteTaskDialog,
  DueDate,
  EditTaskDialog,
  EVERYONE,
  formatDueDate,
  groupTaskThreads,
  NewTaskDialog,
  ReviewActions,
  type TaskRow,
  type ThreadedTask,
  useAssigneeOptions,
  useTaskToggle,
  useVendorOptions,
} from '../components/task-shared.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const ALL = 'all'

export function TasksPage() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const canWrite = can('tasks:write')
  const canReview = can('tasks:review')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<string>('open')
  const [assignee, setAssignee] = useState<string>(ALL)
  const [assignedByMe, setAssignedByMe] = useState(false)
  const [scheduled, setScheduled] = useState(false)
  const [customer, setCustomer] = useState<CustomerLite | null>(null)
  const [vendor, setVendor] = useState<string>(ALL)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<TaskRow | null>(null)
  const [completing, setCompleting] = useState<TaskRow | null>(null)
  const [parentFor, setParentFor] = useState<TaskRow | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpanded = useCallback(
    (id: string) =>
      setExpanded((prev) => {
        const next = new Set(prev)
        if (!next.delete(id)) next.add(id)
        return next
      }),
    [],
  )
  const assignees = useAssigneeOptions()
  const { canSee: canSeeVendors, vendors } = useVendorOptions()
  const toggle = useTaskToggle()

  const filters = {
    q: search || undefined,
    status,
    // The "me" shortcut resolves server-side; everyone and a person's id pass through.
    assignee: assignee === ALL ? undefined : assignee === EVERYONE ? 'everyone' : assignee,
    createdBy: assignedByMe ? ('me' as const) : undefined,
    scheduled: scheduled ? ('only' as const) : undefined,
    customerId: customer?.id,
    vendorId: vendor === ALL ? undefined : vendor,
    pageSize: 50,
  }

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.tasks.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: TaskRow[]; total: number; page: number; pageSize: number }>('/tasks', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    // Colleagues complete shared tasks while this is open, and due dates roll
    // over at midnight — poll like the support queue does.
    refetchInterval: 15_000,
  })
  // How many are waiting on an administrator (t1216) — the banner's count.
  const reviewFilters = { status: 'pending_review', pageSize: 1 }
  const { data: awaiting } = useQuery({
    queryKey: queryKeys.tasks.list(reviewFilters),
    queryFn: () => api.get<{ total: number }>('/tasks', { query: reviewFilters }),
    enabled: canReview,
    refetchInterval: 15_000,
  })
  const awaitingCount = awaiting?.total ?? 0

  // Threads form within what is loaded: a follow-up sits under its parent when
  // the parent is in the list, otherwise it says what it follows up on.
  const rows = useMemo(() => groupTaskThreads(data?.pages.flatMap((p) => p.items) ?? []), [data])
  const total = data?.pages[0]?.total ?? 0
  const filtered =
    Boolean(search) ||
    status !== 'open' ||
    assignee !== ALL ||
    assignedByMe ||
    scheduled ||
    customer !== null ||
    vendor !== ALL

  const columns = useMemo<ColumnDef<ThreadedTask, unknown>[]>(
    () => [
      {
        header: 'Task',
        accessorKey: 'title',
        cell: ({ row }) => (
          <div
            className="flex min-w-0 items-start gap-2"
            style={{ paddingInlineStart: row.original.depth * 20 }}
          >
            <span className="mt-0.5">
              <ExpandToggle
                expanded={expanded.has(row.original.id)}
                onToggle={() => toggleExpanded(row.original.id)}
                label={row.original.taskNumber}
              />
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-1 truncate font-medium">
                {row.original.depth > 0 ? (
                  <CornerDownRight
                    className="size-3.5 shrink-0 text-foreground-subtle"
                    aria-hidden="true"
                  />
                ) : null}
                {row.original.title}
              </p>
              <p className="font-identifier text-xs text-foreground-muted">
                {row.original.taskNumber}
                {row.original.orphan && row.original.parentTaskNumber
                  ? ` · follow-up of ${row.original.parentTaskNumber}`
                  : ''}
              </p>
            </div>
          </div>
        ),
      },
      {
        header: 'Assigned to',
        accessorKey: 'assignedStaffName',
        cell: ({ row }) =>
          row.original.assignedStaffId ? (
            <span>{row.original.assignedStaffName}</span>
          ) : (
            <Badge tone="neutral">Everyone</Badge>
          ),
      },
      {
        header: 'Due',
        accessorKey: 'dueDate',
        cell: ({ row }) => <DueDate task={row.original} />,
      },
      {
        header: 'Starts',
        accessorKey: 'startDate',
        cell: ({ row }) =>
          row.original.startDate ? (
            <span className="text-foreground-muted">{formatDueDate(row.original.startDate)}</span>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Ticket',
        accessorKey: 'supportTicketNumber',
        cell: ({ row }) =>
          row.original.supportTicketId ? (
            <Link
              to="/support-tickets/$ticketId"
              params={{ ticketId: row.original.supportTicketId }}
              className="font-identifier text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.supportTicketNumber}
            </Link>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Rental',
        accessorKey: 'rentalNumber',
        cell: ({ row }) =>
          row.original.rentalId ? (
            <Link
              to="/rentals/$rentalId"
              params={{ rentalId: row.original.rentalId }}
              className="font-identifier text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.rentalNumber}
            </Link>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Customers',
        accessorKey: 'customerName',
        cell: ({ row }) =>
          row.original.customers?.length ? (
            <div className="flex flex-col items-start">
              {row.original.customers.map((c) => (
                <Link
                  key={c.id}
                  to="/customers/$customerId"
                  params={{ customerId: c.id }}
                  className="text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Vendor',
        accessorKey: 'vendorName',
        cell: ({ row }) =>
          row.original.vendorId ? (
            <Link
              to="/vendors/$vendorId"
              params={{ vendorId: row.original.vendorId }}
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.vendorName}
            </Link>
          ) : (
            <span className="text-foreground-subtle">—</span>
          ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <div>
            <Badge tone={TASK_STATUS_TONE[row.original.status] ?? 'neutral'}>
              {humanizeStatus(row.original.status)}
            </Badge>
            {row.original.status !== 'open' && row.original.completedByName ? (
              <p className="mt-0.5 text-xs text-foreground-muted">
                by {row.original.completedByName}
              </p>
            ) : null}
            {row.original.status !== 'open' && row.original.completionNote ? (
              <p
                className="mt-0.5 max-w-64 truncate text-xs text-foreground-muted"
                title={row.original.completionNote}
              >
                “{row.original.completionNote}”
              </p>
            ) : null}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          canWrite ? (
            // The row opens the detail page; these must not also trigger that.
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setEditing(row.original)
                }}
              >
                <Pencil /> Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setParentFor(row.original)
                }}
              >
                <Plus /> Follow-up
              </Button>
              {row.original.status === 'pending_review' && canReview ? (
                <ReviewActions task={row.original} />
              ) : null}
              {row.original.status === 'pending_review' && canReview ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={toggle.isPending}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (row.original.status === 'open') setCompleting(row.original)
                    else toggle.mutate({ id: row.original.id, action: 'reopen' })
                  }}
                >
                  {row.original.status === 'open' ? 'Complete' : 'Reopen'}
                </Button>
              )}
            </div>
          ) : null,
      },
    ],
    [canWrite, canReview, toggle, expanded, toggleExpanded],
  )

  return (
    <Page>
      <PageHeader
        title="Tasks"
        description="To-dos and ticket follow-ups, for one person or for everyone."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New task
            </Button>
          ) : null
        }
      />

      {canReview && awaitingCount > 0 && status !== 'pending_review' ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <span>
            {awaitingCount} {awaitingCount === 1 ? 'task is' : 'tasks are'} awaiting your review.
          </span>
          <Button variant="secondary" size="sm" onClick={() => setStatus('pending_review')}>
            Show {awaitingCount === 1 ? 'it' : 'them'}
          </Button>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search
            className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Title or task number…"
            className="ps-8"
          />
        </div>
        <div className="w-40">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="pending_review">Awaiting review</SelectItem>
              <SelectItem value="done">Done</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger aria-label="Assigned to">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Anyone</SelectItem>
              <SelectItem value={EVERYONE}>Everyone (shared)</SelectItem>
              {assignees.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <CustomerPicker
            value={customer}
            onChange={setCustomer}
            placeholder="Filter by customer…"
          />
        </div>
        {canSeeVendors ? (
          <div className="w-48">
            <Select value={vendor} onValueChange={setVendor}>
              <SelectTrigger aria-label="Vendor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any vendor</SelectItem>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <Switch
            id="tasks-assigned-by-me"
            checked={assignedByMe}
            onCheckedChange={setAssignedByMe}
          />
          <label className="text-sm" htmlFor="tasks-assigned-by-me">
            Assigned by me
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="tasks-scheduled" checked={scheduled} onCheckedChange={setScheduled} />
          <label className="text-sm" htmlFor="tasks-scheduled">
            Scheduled (not started yet)
          </label>
        </div>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        renderExpanded={(row) =>
          expanded.has(row.id) ? <TaskNotesPreview taskId={row.id} /> : null
        }
        onRowClick={(row) => void navigate({ to: '/tasks/$taskId', params: { taskId: row.id } })}
        infinite={{
          total,
          hasMore: Boolean(hasNextPage),
          isFetchingMore: isFetchingNextPage,
          onLoadMore: () => void fetchNextPage(),
        }}
        empty={
          filtered ? (
            <EmptyState
              icon={Search}
              variant="no-results"
              title="No tasks match"
              description="Try a different search, status, person, customer or vendor."
              onClearFilters={() => {
                setSearch('')
                setStatus('open')
                setAssignee(ALL)
                setAssignedByMe(false)
                setScheduled(false)
                setCustomer(null)
                setVendor(ALL)
              }}
            />
          ) : (
            <EmptyState
              icon={ListChecks}
              title="Nothing to do"
              description="Add a task, or add a follow-up from a support ticket."
            />
          )
        }
      />

      <NewTaskDialog open={creating} onOpenChange={setCreating} />
      <NewTaskDialog
        open={parentFor !== null}
        onOpenChange={(open) => !open && setParentFor(null)}
        parentTask={parentFor}
      />
      <CompleteTaskDialog task={completing} onClose={() => setCompleting(null)} />
      <EditTaskDialog task={editing} onOpenChange={(open) => !open && setEditing(null)} />
    </Page>
  )
}
